<template>
  <div class="doughnut-chart-container pt-5 pr-5">
    <Doughnut
      :chart-options="chartOptions"
      :chart-data="chartData"
      :chart-id="chartId"
      :dataset-id-key="datasetIdKey"
      :plugins="[customConnectorPlugin, ...plugins]"
      :css-classes="cssClasses"
      :width="width"
      :height="height"
    />
  </div>
</template>

<script>
import { Doughnut } from 'vue-chartjs'
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  CategoryScale
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'

ChartJS.register(Title, Tooltip, Legend, ArcElement, CategoryScale, ChartDataLabels)

export default {
  name: 'DoughnutChart',
  components: { Doughnut },
  props: {
    chartId: { type: String, default: 'doughnut-chart' },
    datasetIdKey: { type: String, default: 'label' },
    width: { type: Number, default: undefined },
    height: { type: Number, default: undefined },
    cssClasses: { default: '', type: String },
    styles: { type: Object, default: () => {} },
    plugins: { type: Array, default: () => [] },
    screeningData: { type: Array, required: true },
    mainsData: { type: Array, required: true },
    screeningCount: { type: Array, required: true },
    mainsCount: { type: Array, required: true }
  },

  computed: {
    chartData() {
      return {
        labels: ['Pass', 'Fail'],
        datasets: [
          {
            label: 'Screening', // Outer ring
            backgroundColor: ['#5AD684', '#FF78A0'],
            data: this.screeningData,
            cutout: '40%',
            radius: '100%'
          },
          {
            label: 'Mains', // Inner ring
            backgroundColor: ['#5AD684', '#FF78A0'],
            data: this.mainsData,
            cutout: '25%'
          }
        ]
      }
    }
  },

  mounted() {
    this.updateFontSizeForBreakpoint()
    window.addEventListener('resize', this.updateFontSizeForBreakpoint)
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.updateFontSizeForBreakpoint)
  },

  methods: {
    updateFontSizeForBreakpoint() {
      const width = window.innerWidth
      const isSmall = width <= 1380
      if (width <= 1478) {
      // Smaller screens
      this.lineLength = 30;
      this.smallOffset = 25;
    } else if (width <= 1848) {
      // Medium screens
      this.lineLength = 10;
      this.smallOffset = 3;
    } else {
      // Large screens
      this.lineLength = 10;
      this.smallOffset = 3;
    }
      this.chartOptions.plugins.datalabels.font.size = isSmall ? 10 : 12
    }
  },

  data() {
    return {
      lineLength: 10, // default
      smallOffset: 3, // default
      // Custom plugin to draw connector lines for small slices
      customConnectorPlugin: {
        id: 'customConnector',
        afterDatasetsDraw: (chart) => {
          const ctx = chart.ctx
          chart.data.datasets.forEach((dataset, datasetIndex) => {
            const meta = chart.getDatasetMeta(datasetIndex)
            meta.data.forEach((arc, i) => {
              const value = dataset.data[i]
              if (value < 5 && value > 0) {
                const centerX = arc.x
                const centerY = arc.y
                const radius = arc.outerRadius
                const angle = (arc.startAngle + arc.endAngle) / 2

                const startX = centerX + Math.cos(angle) * radius
                const startY = centerY + Math.sin(angle) * radius

                
                const endX = centerX + Math.cos(angle) * (radius + this.lineLength)
                const endY = centerY + Math.sin(angle) * (radius + this.lineLength)

                ctx.save()
                ctx.strokeStyle = '#666'
                ctx.lineWidth = 0.5
                ctx.beginPath()
                ctx.moveTo(startX, startY)
                ctx.lineTo(endX, endY)
                ctx.stroke()
                ctx.restore()
              }
            })
          })
        }
      },

      // ✅ Chart options
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'right',
            labels: {
              font: { size: 12 },
              usePointStyle: true,
              pointStyle: 'rect'
            }
          },
          tooltip: {
            callbacks: {
              label: (tooltipItem) => {
                const datasetLabel = tooltipItem.dataset.label
                const label = tooltipItem.label
                const index = tooltipItem.dataIndex
                const percentage = tooltipItem.raw

                let count = 0
                if (datasetLabel === 'Screening') count = this.screeningCount[index]
                else if (datasetLabel === 'Mains') count = this.mainsCount[index]

                return `${label}: ${percentage}% (${count})`
              }
            }
          },
          datalabels: {
            color: '#000',
            font: { size: 10 },
            formatter: (value) => (value === 0 ? null : `${value}%`),

            // 💡 Move labels outward for small values
            align: (context) => {
              const value = context.dataset.data[context.dataIndex]
              return value < 5 ? 'end' : 'center'
            },
            anchor: (context) => {
              const value = context.dataset.data[context.dataIndex]
              return value < 5 ? 'end' : 'center'
            },
            offset: (context) => {
              const value = context.dataset.data[context.dataIndex]
              return value < 5 ? this.smallOffset : 0
            },
            clip: false,
            padding: 6
          }
        },
        elements: {
          
        }
      }
    }
  }
}
</script>

<style scoped>
.doughnut-chart-container {
  width: 100%;
  height: 100%;
  max-width: 100%;
  margin: auto;
  position: relative;
}
</style>
